import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  alpha,
  useTheme,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import VideocamIcon from '@mui/icons-material/Videocam';
import toast from 'react-hot-toast';
import { analyzeVideo } from '../services/api';

import ProgressBar from '../components/ProgressBar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const steps = ['Upload Video', 'Configure Analysis', 'Analyze'];

const Analyze = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [videoFile, setVideoFile] = useState(null);
  const [analysisConfig, setAnalysisConfig] = useState({
    calibrationDistance: 20.12,
    detectSpin: false,
    detectMultiple: false,
    modelName: 'yolov8n',
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [wsConnection, setWsConnection] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  const onDrop = useCallback((acceptedFiles) => {
    console.log('Files dropped:', acceptedFiles);
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      console.log('File details:', file.name, file.type, file.size);
      if (file.type.startsWith('video/')) {
        setVideoFile(file);
        toast.success(`Video uploaded: ${file.name}`);
        setActiveStep(1);
      } else {
        console.error('Invalid file type:', file.type);
        toast.error('Please upload a valid video file (MP4, AVI, MOV, WEBM, MKV)');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'video/x-msvideo': ['.avi'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
      'video/x-matroska': ['.mkv'],
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024, // 100MB
    onDropRejected: (rejectedFiles) => {
      console.log('Rejected files:', rejectedFiles);
      if (rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === 'file-too-large') {
          toast.error('File is too large. Maximum size is 100MB');
        } else if (error.code === 'file-invalid-type') {
          toast.error('Invalid file type. Please upload a video file (MP4, AVI, MOV, WEBM, MKV)');
        } else {
          toast.error(`Error: ${error.message}`);
        }
      }
    },
  });

  const handleConfigChange = (field) => (event) => {
    setAnalysisConfig({
      ...analysisConfig,
      [field]: event.target.value,
    });
  };

  const handleSwitchChange = (field) => (event) => {
    setAnalysisConfig({
      ...analysisConfig,
      [field]: event.target.checked,
    });
  };

  const handleNext = () => {
    // If we're on step 1 and clicking Next, start the analysis instead of going to step 2
    if (activeStep === 1) {
      startAnalysis();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Complete fixed startAnalysis function:

const startAnalysis = async () => {
  if (!videoFile) {
    toast.error('Please upload a video first');
    return;
  }

  setIsAnalyzing(true);
  setActiveStep(2);
  setProgress(0);
  setProgressMessage('Starting analysis...');
  setUploadProgress(0);

  const formData = new FormData();
  formData.append('video', videoFile);
  formData.append('calibration_distance', analysisConfig.calibrationDistance);
  formData.append('detect_spin', analysisConfig.detectSpin);
  formData.append('detect_multiple', analysisConfig.detectMultiple);
  formData.append('model_name', analysisConfig.modelName);

  try {
    // Create WebSocket connection with temporary ID
    const ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/progress/temp`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data);
        
        // Check message type
        switch (data.type) {
          case 'status':
            // Handle status message
            if (data.data?.status === 'completed') {
              console.log('Analysis already completed');
              handleAnalysisComplete(
                data.data?.result?.analysis_id || data.analysis_id,
                data.data?.result
              );
            } else if (data.data?.status === 'failed') {
              handleAnalysisFailed(data.data?.message || data.data?.error);
            } else if (data.data?.status === 'processing') {
              // Update progress from status
              if (data.data.progress !== undefined) {
                setProgress(data.data.progress);
              }
              if (data.data.message) {
                setProgressMessage(data.data.message);
              }
            }
            break;
            
          case 'progress':
            setProgress(data.progress);
            setProgressMessage(data.message);
            if (data.analysis_id && data.analysis_id !== 'temp') {
              setAnalysisId(data.analysis_id);
            }
            break;
            
          case 'completed':
            handleAnalysisComplete(data.analysis_id, data.data);
            break;
            
          case 'failed':
            handleAnalysisFailed(data.error || data.message);
            break;
            
          default:
            console.log('Unknown message type:', data.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('WebSocket connection error');
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    setWsConnection(ws);

    // Helper functions
    const handleAnalysisComplete = (resultId, resultData) => {
      toast.success('Analysis completed successfully!');
      if (wsConnection) {
        wsConnection.close();
      }
      setIsAnalyzing(false);
      
      // Show completion for a moment before navigating
      setProgress(100);
      setProgressMessage('Analysis complete! Redirecting to results...');
      
      setTimeout(() => {
        if (resultId) {
          setAnalysisId(resultId);
          navigate(`/results/${resultId}`);
        } else if (analysisId) {
          navigate(`/results/${analysisId}`);
        }
      }, 1500);
    };
    
    const handleAnalysisFailed = (errorMessage) => {
      toast.error(`Analysis failed: ${errorMessage || 'Unknown error'}`);
      if (wsConnection) {
        wsConnection.close();
      }
      setIsAnalyzing(false);
      setActiveStep(1);
    };

    // Start API analysis with upload progress tracking
    const response = await analyzeVideo(formData, (uploadPercent) => {
      setUploadProgress(uploadPercent);
    });

    if (response.analysis_id) {
      setAnalysisId(response.analysis_id);
      // Update WebSocket to use actual analysis ID
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
        const newWs = new WebSocket(`${process.env.REACT_APP_WS_URL}/progress/${response.analysis_id}`);
        
        // Reattach event handlers to new WebSocket
        newWs.onopen = () => {
          console.log('WebSocket reconnected with analysis ID');
        };
        
        newWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message (with ID):', data);
            
            if (data.type === 'progress') {
              setProgress(data.progress);
              setProgressMessage(data.message);
            } else if (data.type === 'completed' || (data.type === 'status' && data.data?.status === 'completed')) {
              handleAnalysisComplete(data.analysis_id, data.data?.result || data.data);
            } else if (data.type === 'failed' || (data.type === 'status' && data.data?.status === 'failed')) {
              handleAnalysisFailed(data.error || data.data?.message);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        newWs.onerror = (error) => {
          console.error('WebSocket error:', error);
          toast.error('WebSocket connection error');
        };
        
        newWs.onclose = () => {
          console.log('WebSocket disconnected');
        };
        
        setWsConnection(newWs);
      }
    }

  } catch (error) {
    console.error('Analysis error:', error);
    toast.error(error.message || error.detail || 'Analysis failed. Please try again.');
    setIsAnalyzing(false);
    setActiveStep(1);
    
    if (wsConnection) {
      wsConnection.close();
    }
  }
};

  const handleReset = () => {
    setVideoFile(null);
    setAnalysisConfig({
      calibrationDistance: 20.12,
      detectSpin: false,
      detectMultiple: false,
      modelName: 'yolov8n',
    });
    setIsAnalyzing(false);
    setAnalysisId(null);
    setProgress(0);
    setProgressMessage('');
    setUploadProgress(0);
    setActiveStep(0);
    
    if (wsConnection) {
      wsConnection.close();
      setWsConnection(null);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Upload Cricket Bowling Video
            </Typography>
            <Typography color="text.secondary" paragraph sx={{ mb: 4 }}>
              Upload a video of a cricket delivery for analysis. Our system will detect the ball, track its trajectory, and calculate detailed metrics.
            </Typography>

            <Paper
              {...getRootProps()}
              sx={{
                p: 6,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : alpha(theme.palette.text.secondary, 0.3),
                backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.background.paper, 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateY(-2px)',
                },
                borderRadius: 3,
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 3, opacity: 0.8 }} />
              {isDragActive ? (
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Drop the video here...
                </Typography>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Drag & drop video file here
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    or{' '}
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          textDecoration: 'none',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        document.querySelector('input[type="file"]').click();
                      }}
                    >
                      click to browse files
                    </Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Supported formats: MP4, AVI, MOV, WEBM, MKV
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Maximum file size: 100MB
                  </Typography>
                </>
              )}
            </Paper>

            {videoFile && (
              <Card sx={{ mt: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <VideocamIcon color="primary" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {videoFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Size: {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => setVideoFile(null)}>
                      Remove
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!videoFile}
                startIcon={<SettingsIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Configure Analysis
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Analysis Configuration
            </Typography>
            <Typography color="text.secondary" paragraph sx={{ mb: 4 }}>
              Customize the analysis parameters for optimal results.
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Calibration Distance (meters)"
                  type="number"
                  value={analysisConfig.calibrationDistance}
                  onChange={handleConfigChange('calibrationDistance')}
                  helperText="Standard cricket pitch length is 20.12 meters"
                  InputProps={{
                    inputProps: { min: 1, max: 100, step: 0.01 },
                  }}
                  variant="outlined"
                  sx={{ mb: 3 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Detection Model"
                  value={analysisConfig.modelName}
                  onChange={handleConfigChange('modelName')}
                  SelectProps={{
                    native: true,
                  }}
                  variant="outlined"
                  sx={{ mb: 3 }}
                >
                  <option value="yolov8n">YOLOv8 Nano (Fastest)</option>
                  <option value="yolov8s">YOLOv8 Small</option>
                  <option value="yolov8m">YOLOv8 Medium</option>
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={analysisConfig.detectSpin}
                          onChange={handleSwitchChange('detectSpin')}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Detect Spin Rate
                          </Typography>
                          <Typography variant="caption" color="text-secondary">
                            Estimate spin rate in RPM (requires clear ball visibility)
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
                
                <Card variant="outlined">
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={analysisConfig.detectMultiple}
                          onChange={handleSwitchChange('detectMultiple')}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Detect Multiple Deliveries
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Analyze multiple deliveries in a single video
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 4, mb: 3 }}>
              <AlertTitle>Tip</AlertTitle>
              For best results, ensure the video clearly shows the ball throughout its flight.
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button onClick={handleBack} sx={{ px: 4 }}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={startAnalysis}
                startIcon={<PlayArrowIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Start Analysis
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Analyzing Video
            </Typography>

            

{isAnalyzing ? (
  <>
    {uploadProgress < 100 && (
      <ProgressBar 
        progress={uploadProgress} 
        message={`Uploading video... ${uploadProgress}%`}
      />
    )}
    
    <ProgressBar 
      progress={progress} 
      message={progressMessage}
      analysisId={analysisId}
    />
    
    <Box sx={{ mt: 6, textAlign: 'center' }}>
      <CircularProgress 
        size={80} 
        thickness={4} 
        sx={{ 
          color: 'primary.main',
          animation: 'spin 2s linear infinite',
        }} 
      />
      <Typography variant="h6" sx={{ mt: 3, fontWeight: 600 }}>
        Analysis in progress...
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        This may take a few minutes depending on video length
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {analysisId && `Analysis ID: ${analysisId}`}
      </Typography>
    </Box>
  </>
) : (
  <Alert 
    severity="success" 
    sx={{ 
      mt: 2,
      '& .MuiAlert-icon': {
        fontSize: 32,
      },
    }}
  >
    <AlertTitle>Analysis Complete!</AlertTitle>
    Your video has been analyzed successfully. Click "View Results" to see the detailed analysis.
  </Alert>
)}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6 }}>
              <Button 
                onClick={handleBack} 
                disabled={isAnalyzing}
                sx={{ px: 4 }}
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  onClick={handleReset} 
                  sx={{ px: 4 }}
                  disabled={isAnalyzing}
                >
                  Start Over
                </Button>
                {!isAnalyzing && analysisId && (
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/results/${analysisId}`)}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    View Results
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        sx={{
          p: { xs: 3, md: 5 },
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
          Analyze Cricket Delivery
        </Typography>
        <Typography color="text.secondary" paragraph sx={{ mb: 4 }}>
          Upload a cricket bowling video to analyze ball speed, swing, trajectory, and more
        </Typography>

        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mt: 4, 
            mb: 6,
            '& .MuiStepLabel-root .Mui-active': {
              color: theme.palette.primary.main,
            },
            '& .MuiStepLabel-root .Mui-completed': {
              color: theme.palette.primary.main,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </Paper>
    </Container>
  );
};

export default Analyze;